import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Globe, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { ErrorState } from '@/components/states';
import { MediaChannelsCard } from '@/features/settings/media-channels-card';
import { useSettingsQuery, useUpdateSettings } from '@/hooks/use-settings';
import { describeError } from '@/lib/api-client';
import { formatAbsoluteTitle, formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { AppSettings, CatalogueVisibility } from '@/types/api';

/**
 * Runtime settings. The API applies a change to the very next request and authorises it
 * server-side; this page only chooses what to send.
 */
export function SettingsPage() {
  const settings = useSettingsQuery();

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Changes apply to everyone as soon as you save them, without a deploy.
        </p>
      </header>

      {settings.isError ? (
        <ErrorState
          title="Could not load the settings"
          error={settings.error}
          onRetry={() => void settings.refetch()}
        />
      ) : settings.isPending ? (
        <Skeleton className="h-80 w-full" aria-label="Loading settings" />
      ) : (
        <>
          <VisibilityCard settings={settings.data} />
          <MediaChannelsCard settings={settings.data} />
        </>
      )}
    </div>
  );
}

function VisibilityCard({ settings }: { settings: AppSettings }) {
  const update = useUpdateSettings();
  const { toast } = useToast();
  const [choice, setChoice] = useState<CatalogueVisibility>(settings.catalogueVisibility);
  const [confirmingPublic, setConfirmingPublic] = useState(false);

  const current = settings.catalogueVisibility;
  const changed = choice !== current;

  const save = (visibility: CatalogueVisibility) => {
    update.mutate(
      { catalogueVisibility: visibility },
      {
        onSuccess: () => {
          setConfirmingPublic(false);
          toast({
            title:
              visibility === 'private'
                ? 'The catalogue is now private'
                : 'The catalogue is now public',
            description:
              visibility === 'private'
                ? 'Visitors who are not signed in get the sign-in page.'
                : 'Anyone with the address can browse it without signing in.',
            variant: 'success',
          });
        },
        onError: (error) =>
          toast({
            title: 'Could not save the settings',
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  // Opening the catalogue exposes every link in it, so it takes a second, deliberate step.
  // Closing it never does: the safe direction should be one click.
  const onSave = () => {
    if (choice === 'public') setConfirmingPublic(true);
    else save(choice);
  };

  return (
    <Card>
      <CardHeader className="gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">Catalogue visibility</CardTitle>
          <Badge variant={current === 'private' ? 'success' : 'warning'}>Currently {current}</Badge>
        </div>
        <CardDescription>Who can browse the links collected from Slack.</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="sr-only">Catalogue visibility</legend>
          <VisibilityOption
            value="private"
            checked={choice === 'private'}
            onSelect={setChoice}
            icon={<Lock className="h-4 w-4" />}
            title="Private"
            tagline="Sign-in required"
            badge={<Badge variant="secondary">Recommended</Badge>}
          >
            Only people signed in with a Seez Google account can see the catalogue. Everyone else
            gets the sign-in page, and links unfurled in Slack or elsewhere show a sign-in card,
            never what the link is about.
          </VisibilityOption>
          <VisibilityOption
            value="public"
            checked={choice === 'public'}
            onSelect={setChoice}
            icon={<Globe className="h-4 w-4" />}
            title="Public"
            tagline="Anyone with the address"
          >
            Anyone can browse links without signing in. Who shared each link, and in which channel,
            stays hidden; statistics and admin pages still require sign-in.
          </VisibilityOption>
        </fieldset>

        <div className="flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {settings.updatedBy && settings.updatedAt ? (
              <>
                Last changed by <span className="font-medium">{settings.updatedBy}</span>{' '}
                <time dateTime={settings.updatedAt} title={formatAbsoluteTitle(settings.updatedAt)}>
                  {formatRelative(settings.updatedAt)}
                </time>
                .
              </>
            ) : (
              'Never changed: the catalogue stays private until an administrator makes it public.'
            )}
          </p>
          <div className="flex gap-2 sm:shrink-0">
            {changed ? (
              <Button type="button" variant="ghost" onClick={() => setChoice(current)}>
                Discard
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={!changed}
              loading={update.isPending && !confirmingPublic}
              onClick={onSave}
            >
              Save changes
            </Button>
          </div>
        </div>
      </CardContent>

      <Dialog open={confirmingPublic} onOpenChange={setConfirmingPublic}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make the catalogue public?</DialogTitle>
            <DialogDescription>
              Anyone who has the address will be able to browse every link in the catalogue —
              titles, summaries and screenshots — without signing in. Make sure nothing in it is
              sensitive first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmingPublic(false)}>
              Keep it private
            </Button>
            <Button type="button" loading={update.isPending} onClick={() => save('public')}>
              Make public
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/** A radio button drawn as a selectable card. The native input keeps keyboard and screen-reader behaviour. */
function VisibilityOption({
  value,
  checked,
  onSelect,
  icon,
  title,
  tagline,
  badge,
  children,
}: {
  value: CatalogueVisibility;
  checked: boolean;
  onSelect: (value: CatalogueVisibility) => void;
  icon: ReactNode;
  title: string;
  tagline: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  const descriptionId = useId();

  return (
    <label
      className={cn(
        'flex cursor-pointer flex-col gap-3 rounded-lg border p-4 transition-colors',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background',
        checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
      )}
    >
      <input
        type="radio"
        name="catalogue-visibility"
        value={value}
        checked={checked}
        onChange={() => onSelect(value)}
        aria-describedby={descriptionId}
        className="sr-only"
      />
      <span className="flex items-start justify-between gap-3">
        <span className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-md',
              checked
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground',
            )}
          >
            {icon}
          </span>
          <span className="flex flex-col">
            <span className="font-medium">{title}</span>
            <span className="text-xs text-muted-foreground">{tagline}</span>
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
            checked ? 'border-primary' : 'border-input',
          )}
        >
          {checked ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
        </span>
      </span>
      <span id={descriptionId} className="text-sm text-muted-foreground">
        {children}
      </span>
      {badge ? <span>{badge}</span> : null}
    </label>
  );
}
